import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AdminButton, AdminPagination, AdminSearchInput } from "@/components/admin";
import { formatDateTime } from "@/lib/format";
import { getSoundEventStatusLabel, getSoundRiskDisplay } from "@/lib/soundDisplay";
import type { SoundAnalysisV2ListItem } from "@/lib/api";

interface SoundTableProps {
  records: SoundAnalysisV2ListItem[];
  searchQuery: string;
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onSearchChange: (v: string) => void;
  onRefresh: () => void;
}

const toneStyle: Record<string, string> = {
  danger: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-orange-50 text-orange-700 border-orange-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  neutral: "bg-slate-50 text-slate-600 border-slate-200",
};

const formatConfidence = (value: number | null) => {
  if (value == null) return "-";
  return `${Math.round(Math.max(0, Math.min(value, 1)) * 100)}%`;
};

const soundTypeLabel = (record: SoundAnalysisV2ListItem) => record.representativeDisplayLabel ?? record.representativeGroupCode ?? "-";
const soundSubTypeLabel = (record: SoundAnalysisV2ListItem) => record.representativeLabelCode ?? record.representativeModelLabelCode ?? "-";

const analysisLabel = (record: SoundAnalysisV2ListItem) => {
  return getSoundEventStatusLabel(record.eventStatusCode);
};

const sourceLabel = (record: SoundAnalysisV2ListItem) => record.candidateContractVersion;

const SoundTable = ({
  records,
  searchQuery,
  loading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onSearchChange,
  onRefresh,
}: SoundTableProps) => {
  const navigate = useNavigate();
  const pageItems = useMemo(() => {
    let result = records;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((record) =>
        [
          record.seniorName,
          record.seniorUserAccountId,
          record.subjectKey,
          record.deviceName,
          record.deviceId,
          soundTypeLabel(record),
          soundSubTypeLabel(record),
          analysisLabel(record),
          sourceLabel(record),
        ]
          .filter((value) => value != null)
          .some((value) => String(value).toLowerCase().includes(q)),
      );
    }
    return result.sort((left, right) => right.analyzedAt.localeCompare(left.analyzedAt));
  }, [records, searchQuery]);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <i className="ri-list-check text-sm text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">소리 분석 결과 목록</h3>
          <span className="text-xs text-slate-400">총 {totalCount}건 · 현재 페이지 {records.length}건</span>
          {searchQuery.trim() && <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-600">현재 페이지 검색 중</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AdminSearchInput
            value={searchQuery}
            onChange={(nextValue) => {
              onSearchChange(nextValue);
              onPageChange(1);
            }}
            placeholder="현재 페이지 내 이름·subjectKey·소리 검색"
            className="w-64"
          />
          <AdminButton
            variant="secondary"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
            className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-60"
          >
            <i className={`ri-refresh-line text-xs ${loading ? "animate-spin" : ""}`} />
            새로고침
          </AdminButton>
        </div>
      </div>

      <div className="overflow-x-auto border-b border-slate-200 bg-slate-50">
        <div className="grid min-w-[61.25rem] grid-cols-12 gap-2 px-4 py-2.5">
          <div className="col-span-2 text-xs font-semibold text-slate-500">Senior/Subject</div>
          <div className="col-span-2 text-xs font-semibold text-slate-500">소리 유형</div>
          <div className="col-span-2 text-xs font-semibold text-slate-500">분석 결과</div>
          <div className="col-span-1 text-xs font-semibold text-slate-500">상태</div>
          <div className="col-span-1 text-xs font-semibold text-slate-500">신뢰도</div>
          <div className="col-span-2 text-xs font-semibold text-slate-500">기기</div>
          <div className="col-span-1 text-xs font-semibold text-slate-500">분석 시각</div>
          <div className="col-span-1 text-right text-xs font-semibold text-slate-500">상세</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[61.25rem] divide-y divide-slate-100">
          {pageItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <i className="ri-sound-module-line mb-3 text-2xl" />
              <p className="text-sm font-medium">{loading ? "소리 분석 결과를 불러오는 중입니다." : "해당 조건의 분석 기록이 없습니다."}</p>
            </div>
          ) : (
            pageItems.map((record) => {
              const analysis = getSoundRiskDisplay(record.riskLevelCode);
              const confidence = record.representativeConfidence == null ? 0 : Math.max(0, Math.min(record.representativeConfidence, 1)) * 100;

              return (
                <div key={record.soundAnalysisEventId} className="grid grid-cols-12 items-center gap-2 px-4 py-2.5 transition-colors hover:bg-slate-50/80">
                  <div className="col-span-2 min-w-0">
                    <p className="truncate text-xs font-semibold text-slate-800">{record.seniorName ?? record.subjectKey ?? "식별 정보 없음"}</p>
                    <p className="truncate text-xs text-slate-400">{record.subjectKey ?? (record.seniorUserAccountId == null ? "-" : `ID ${record.seniorUserAccountId}`)}</p>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <span className="block truncate text-xs text-slate-700">{soundTypeLabel(record)}</span>
                    <span className="block truncate text-xs text-slate-400">{soundSubTypeLabel(record)}</span>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <span className="block truncate text-xs text-slate-700">{analysisLabel(record)}</span>
                    <span className="block truncate text-xs text-slate-400">{sourceLabel(record)}</span>
                  </div>
                  <div className="col-span-1 min-w-0">
                    <span className={`inline-flex max-w-full items-center truncate rounded border px-1.5 py-0.5 text-xs font-medium ${toneStyle[analysis.tone]}`}>
                      {analysis.label}
                    </span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-bold text-slate-700">{formatConfidence(record.representativeConfidence)}</span>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-blue-500" style={{ width: `${confidence}%` }} />
                    </div>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <p className="truncate text-xs text-slate-700">{record.deviceName ?? (record.deviceId ? `기기 #${record.deviceId}` : "기기 미지정")}</p>
                    {record.deviceId != null && <p className="truncate text-xs text-slate-400">기기 ID {record.deviceId}</p>}
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs text-slate-600">{formatDateTime(record.analyzedAt, 5, 16)}</span>
                  </div>
                  <div className="col-span-1 flex justify-end gap-1">
                    <AdminButton
                      variant="soft"
                      size="sm"
                      onClick={() => navigate(`/admin/sound-analysis/${record.soundAnalysisEventId}`)}
                      className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100"
                    >
                      상세
                    </AdminButton>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <AdminPagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} pageSize={pageSize} onPageChange={onPageChange} />
    </div>
  );
};

export default SoundTable;
